import { NextComponentType } from 'next'
import * as React from 'react'
import { SEMIndexPageTemplateProps, setContentWithFallbacks } from '../../components/SEMPage/sem-old'
import { getReviewIODataFromNest } from '../../asyncLoaders/nest'
import { useRouter } from 'next/router'
import { propOr } from 'ramda'
import * as changeCase from 'capital-case'
import { CityPhoneNumberData } from '../../components/SEMPage/CityPhoneNumberData'
import { formatPhoneNumber } from '../../components/utils/string-utils'
import { getTitleFromUTMS } from '../../components/SEMPage/getTitleFromUTMS'
import Layout from '../../components/Layout'
import Head from 'next/head'
import HeaderSection from '../../components/SEMPage/HeaderSection'
import { twDefaultInitialWizardFormValues } from '../../components/Forms/ScheduleServiceMultiStep/NoEmailScheduleServiceFormMultiStep'
import { HowItWorksGreenCheckSection } from '../../components/SEMPage/HowItWorksGreenCheckSection'
import { ServicesHowItWorksSection } from '../../components/SEMPage/ServicesHowItWorksSection'
import { faMoneyCheckEditAlt } from '@fortawesome/pro-duotone-svg-icons'
import { faCalendarCheck } from '@fortawesome/pro-duotone-svg-icons/faCalendarCheck'
import { faCarMechanic } from '@fortawesome/pro-duotone-svg-icons/faCarMechanic'
import { ImageSection } from '../../components/SEMPage/ImageSection'
import { ReviewsV2Section } from '../../components/ReviewsV2Section'
import { HappinessGuaranteeSection } from '../../components/HappinessGuaranteeSection'
import { CallUsNowBar } from '../../components/CallUsNowBar'
import Link from 'next/link'
import { LogOnMount, Segment } from '../../lib/segment'

const MMSEMPageTemplate: React.FC<SEMIndexPageTemplateProps> = ({
  phoneNumber,
  formalCity,
  phoneNumberTxt,
  type = 'generic',
  content,
  make,
  rating,
  reviewCount,
}) => {
  const headerTitle = getTitleFromUTMS({
    formalCity,
    phoneNumber,
    phoneNumberTxt,
    type,
    content,
    make,
    rating,
    reviewCount,
  })
  return (
    <Layout
      showFooter={false}
      showGetQuote={false}
      phoneNumber={phoneNumber}
      phoneNumberTxt={phoneNumberTxt}
      title={`Schedule Auto Repair In ${formalCity} | Auto Repair Services Starting At $99 | NuBrakes`}
    >
      <Head>
        <meta
          name="description"
          key={'description'}
          content={`Schedule a top rated ${formalCity} Auto Repair Service in minutes from NuBrakes. NuBrakes is a mobile brake repair service based in Austin, TX. Our mobile brake technicians arrive on-site at your office, home or apartment fully equipped to handle your auto issues.`}
        />
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <Segment>
        <LogOnMount eventType="Page Viewed" />
        <HeaderSection
          rating={rating}
          formInitialProps={{
            ...twDefaultInitialWizardFormValues,
            'form-name': 'semScheduleServiceForm',
          }}
          reviewCount={reviewCount}
          title={headerTitle}
        />
        <HowItWorksGreenCheckSection
          title={`Why NuBrakes?`}
          first={{
            title: `We Come To You`,
            subtitle: `Our mobile mechanics deliver top-rated auto repairs at your home or office for up to 40% less than a repair shop.`,
          }}
          second={{
            title: `Better Prices`,
            subtitle: `We're 40% more affordable on average compared to the dealerships and shops so you'll be saving time and money.`,
          }}
          third={{
            title: `Same-Day Availability`,
            subtitle: `Need your vehicle repaired today? Same-day service is typically available — contact us to schedule!`,
          }}
        />
        <ServicesHowItWorksSection
          phoneNumber={phoneNumber}
          title={`How NuBrakes Works`}
          description={`Schedule top-rated auto repair services in minutes.`}
          steps={[
            {
              title: 'Get Your Free Repair Quote',
              description:
                'Let us know what’s going on with your vehicle and we’ll send you a free, no obligation repair quote.',
              image: faMoneyCheckEditAlt,
            },
            {
              title: 'Select Your Repair Date',
              description: `Availability as early as today. Choose a repair date and time that works around your schedule.`,
              image: faCalendarCheck,
            },
            {
              title: 'Enjoy Safer, Better Driving',
              description:
                'Our mobile mechanics will ensure that your vehicle is ready to hit the road and get you back to driving worry-free!',
              image: faCarMechanic,
            },
          ]}
          buttonText={`Get A Quote`}
        />
        <ImageSection />
        <ReviewsV2Section
          title={`Over 1000+ 5-Star Reviews`}
          first={{
            review: `We received fantastic, quick service, at a fair price. We got quite a few quotes and NuBrakes was the best price and it turns out, the quickest and best service. Our technician, Mike, was knowledgeable and friendly and got the job done quickly - all at my office. We would definitely recommend them and use the service again.`,
            from: `${formalCity}`,
            reviewer: `Tami F.`,
          }}
          second={{
            from: `${formalCity}`,
            reviewer: `Clinton B.`,
            review: `This service is awesome. You are paying for fast, convenient and good quality work. Communication with Walker was immediate and the technician Micheal was done in half the time that was quoted. I will use NuBrakes again in the future and recommend it to everyone. I mean, they come to you, that was what sold it for me.`,
          }}
          third={{
            review: `Without a doubt would recommend NuBrakes to anyone that needs brakes done. Service tech showed up on time and was professional and quick. And the conveniences having them come to you is hard to beat. Not to mention the price is close to half what you’d pay at a pep boys or any other body shop. Can’t beat it.`,
            from: `${formalCity}`,
            reviewer: `William T.`,
          }}
        />
        <HappinessGuaranteeSection
          title={'Happiness Guaranteed'}
          description={
            <>
              Our mobile mechanics have decades of combined experience, and all services are backed by our{' '}
              <Link href={'/warranty'}>NuBrakes Guarantee</Link>.
            </>
          }
        />
        <CallUsNowBar phoneNumber={phoneNumber} title={`Schedule A Repair Or Get A Quote Now.`} />
      </Segment>
    </Layout>
  )
}

const MMSEMPage: NextComponentType<any, any, { rating: string; reviewCount: number; sessionId: string }> = ({
  rating,
  reviewCount,
}) => {
  const router = useRouter()
  const { query } = router
  const city: string = propOr('', 'city', query)
  const type: string = propOr('', 'type', query)
  const make: string = propOr('', 'make', query)
  let content = String(query.utm_content)
  content = setContentWithFallbacks(type, content)
  const formalCity = changeCase.capitalCase(city ? city : 'Austin')
  const phoneNumber = city ? CityPhoneNumberData[city!].phoneNumber : CityPhoneNumberData.default.phoneNumber
  if (city) {
    const phoneNumberTxt = formatPhoneNumber(phoneNumber)!
    return (
      <MMSEMPageTemplate
        rating={rating}
        reviewCount={reviewCount}
        phoneNumberTxt={phoneNumberTxt}
        phoneNumber={phoneNumber}
        formalCity={formalCity}
        content={content}
        type={type}
        make={make}
      />
    )
  }
  return (
    <MMSEMPageTemplate
      rating={rating}
      reviewCount={reviewCount}
      formalCity={formalCity ? formalCity : 'Austin'}
      phoneNumber={phoneNumber ? phoneNumber : '5126402560'}
      content={content}
      type={type}
      make={make}
      phoneNumberTxt={phoneNumber ? formatPhoneNumber(phoneNumber)! : '(512) 640-2560'}
    />
  )
}

export const getStaticProps = async function () {
  const { reviewCount, rating } = await getReviewIODataFromNest()
  return { props: { reviewCount, rating }, revalidate: 60 * 60 * 24 }
}

export default MMSEMPage
